import { IEpubPublication } from "@colibrio/colibrio-reader-framework/colibrio-core-publication-epub";
import { VanillaReaderPublication } from "../../VanillaReader/VanillaReaderPublication";
import { VanillaReaderEventBus } from "../../VanillaReaderEventBus/VanillaReaderEventBus";

export declare type AmbientAudioCallback = (ev: Event | string, currentlyPlaying: string | undefined) => void;
export class AmbientAudioMediaPlayer {

    playerIsAttachedToView: boolean = false;
    private _vanillaPublication: VanillaReaderPublication | undefined;
    private _audioElement = document.createElement('audio');
    private _currentlyPlaying: string | undefined;
    private _onEndReachedCallback: AmbientAudioCallback | undefined;
    private _onPlaybackPausedCallback: AmbientAudioCallback | undefined;
    private _onPlaybackStartedCallback: AmbientAudioCallback | undefined;
    private _onPlaybackAbortedCallback: AmbientAudioCallback | undefined;
    private _onPlaybackErrorCallback: AmbientAudioCallback | undefined;
    private _timeUpdateTimeoutHandle: number | undefined;

    constructor(vanillaPublication: VanillaReaderPublication) {
        this._vanillaPublication = vanillaPublication;


        this._audioElement.onerror = (ev) => {
            if (this._currentlyPlaying && this._onPlaybackErrorCallback) {
                this._onPlaybackErrorCallback(ev, this._currentlyPlaying);
            }
        }

        this._audioElement.onpause = (ev) => {
            if (this._currentlyPlaying && this._onPlaybackPausedCallback) {
                this._onPlaybackPausedCallback(ev, this._currentlyPlaying);
            }

        }

        this._audioElement.onabort = (ev) => {
            if (this._currentlyPlaying && this._onPlaybackAbortedCallback) {
                this._onPlaybackAbortedCallback(ev, this._currentlyPlaying);
            }

        }

        this._audioElement.onended = (ev) => {
            if (this._currentlyPlaying) {

                if (this._onEndReachedCallback) {
                    this._onEndReachedCallback(ev, this._currentlyPlaying);
                }
            }
        }

        this._audioElement.oncanplaythrough = (_ev) => {
        }

        this._audioElement.oncanplay = (_ev) => {
        }

        this._audioElement.ondurationchange = (_ev) => {
        }

        this._audioElement.onplay = (ev) => {
            if (this._currentlyPlaying && this._onPlaybackStartedCallback) {
                this._onPlaybackStartedCallback(ev, this._currentlyPlaying);
            }
        }

        this._audioElement.onplaying = (_ev) => {
        }

        this._audioElement.ontimeupdate = (_ev) => {
            if (this._timeUpdateTimeoutHandle === undefined) {
                this._timeUpdateTimeoutHandle = setTimeout(() => {
                    this._timeUpdateTimeoutHandle = undefined;
                    if (!this._currentlyPlaying) {
                        return;
                    }
                }, 500)
            }
        }

        this._audioElement.onseeking = (_ev) => {
        }

        this._audioElement.onseeked = (_ev) => {
        }

        this._audioElement.onratechange = (_ev) => {
        }

        this._audioElement.onprogress = (_ev) => {
        }
    }

    onPlaybackStarted(callback: AmbientAudioCallback): void {
        this._onPlaybackStartedCallback = callback;
    }

    onPlaybackPaused(callback: AmbientAudioCallback): void {
        this._onPlaybackPausedCallback = callback;
    }

    onPlaybackAborted(callback: AmbientAudioCallback): void {
        this._onPlaybackAbortedCallback = callback;
    }

    onPlaybackEndReached(callback: AmbientAudioCallback): void {
        this._onEndReachedCallback = callback;
    }

    onPlaybackError(callback: AmbientAudioCallback): void {
        this._onPlaybackErrorCallback = callback;
    }

    get audioElement(){
       return this._audioElement;
    }

    set playbackRate(rate: number) {
        this._audioElement.playbackRate = rate;
    }

    get playbackRate(): number {
        return this._audioElement.playbackRate;
    }

    get volume(): number {
        return this._audioElement.volume;
    }

    set volume(volume: number) {
        this._audioElement.volume = volume;
    }

    get muted(): boolean {
        return this._audioElement.muted;
    }

    set muted(muted: boolean) {
        this._audioElement.muted = muted;
    }

    getCurrentlyPlaying(): string | undefined {
        return this._currentlyPlaying;
    }

    async setCurrentlyPlaying(audio: string | undefined): Promise<void> {

        if (!audio) {
            this.unset();
            return;
        }

        if (this._audioElement.src && this._currentlyPlaying === audio) {
            console.log('AmbientAudioAudioElementRenderer.setCurrentlyPlaying(): Already playing this audio.')
            return;
        }

        this._currentlyPlaying = audio;
        let srcPublication = this._vanillaPublication?.getColibrioReaderPublication().getSourcePublication() as IEpubPublication;
        let opfBasePath = srcPublication.getOpfDocumentUrl().pathname.slice(0, srcPublication.getOpfDocumentUrl().pathname.lastIndexOf('/'));
        let audioUrl = audio?.startsWith('http') ? audio : await this._vanillaPublication?.fetchResourceBlobUrl(`${opfBasePath}/${audio}`);
        if (!this._audioElement.paused){
            this._audioElement.src = audioUrl!;
            this._audioElement.play();
        } else {
            this._audioElement.src = audioUrl!;
        }
        VanillaReaderEventBus.dispatchEvent(VanillaReaderEventBus.createEvent('AMBIENT_AUDIO_READY', {isPlaying: !this._audioElement.paused}));
    }

    async play(): Promise<void> {
        return this._audioElement.play();
    }

    pause(): void {
        this._audioElement.pause();
    }

    unset(): void {
        this._audioElement.removeAttribute('src');
    }

}
